'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useRef, forwardRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Drawer } from '@/components/atoms/Drawer';
import { IconPin, CloseRoundIcon, PenIcon, IconAlarm } from '@/components/atoms/Icons';
import { fmtTimeRangeFromStrings, combineDateAndTime, monthDay, fmtDate } from '@/shared/dates';
import { ordinal, formatLocationName, smartTruncate } from '@/shared/text';
import { haversineMiles, formatMiles, getDirectionsUrl } from '@/shared/geo';
import UIButton from '@/components/atoms/UIButtons/UIButton';
import ClockMiles from 'assets/icons/clock.svg';
import HeartSaveButton from '@/components/organisms/HeartSaveButton';
import { useExplore } from './ExploreProvider';
import { getMarkerIcon, TypeBadge } from './markerIcons';
import { isHeartType } from '../constants';
import { MARKER_TYPES } from '../constants';

/**
 * Hook to calculate distance and directions URL for an item
 */
export function useItemLocation(item) {
  const $ = _c(14);
  const {
    userLocation,
    locationPermission
  } = useExplore();
  let t0;
  if ($[0] !== item?.location?.lat || $[1] !== item?.location?.lng) {
    t0 = Number.isFinite(item?.location?.lat) && Number.isFinite(item?.location?.lng);
    $[0] = item?.location?.lat;
    $[1] = item?.location?.lng;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasCoordinates = t0;
  let coordinates;
  let formattedDistance;
  let t1;
  if ($[3] !== hasCoordinates || $[4] !== item || $[5] !== locationPermission || $[6] !== userLocation) {
    coordinates = hasCoordinates ? {
      lat: item.location.lat,
      lng: item.location.lng
    } : null;
    const distanceMiles = locationPermission === "granted" && userLocation && coordinates ? haversineMiles([userLocation.lat, userLocation.lng], [coordinates.lat, coordinates.lng]) : null;
    formattedDistance = distanceMiles ? formatMiles(distanceMiles) : null;
    t1 = coordinates?.lat && coordinates?.lng ? getDirectionsUrl(coordinates.lat, coordinates.lng, item.name) : null;
    $[3] = hasCoordinates;
    $[4] = item;
    $[5] = locationPermission;
    $[6] = userLocation;
    $[7] = coordinates;
    $[8] = formattedDistance;
    $[9] = t1;
  } else {
    coordinates = $[7];
    formattedDistance = $[8];
    t1 = $[9];
  }
  const directionsUrl = t1;
  let t2;
  if ($[10] !== coordinates || $[11] !== directionsUrl || $[12] !== formattedDistance) {
    t2 = {
      formattedDistance,
      directionsUrl,
      coordinates
    };
    $[10] = coordinates;
    $[11] = directionsUrl;
    $[12] = formattedDistance;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}

/**
 * Get details page URL for an entity
 */
export function getDetailsUrl(item) {
  if (!item) return null;
  switch (item.type) {
    case MARKER_TYPES.HEART:
    case MARKER_TYPES.SAVED_HEART:
    case MARKER_TYPES.VISITED_HEART:
    case MARKER_TYPES.SAVED_VISITED_HEART:
      return item.slug ? `/hearts/${item.slug}` : `/hearts/${item.id}`;
    case MARKER_TYPES.ATTRACTION:
      return item.slug ? `/attraction/${item.slug}` : `/attraction/${item.id}`;
    case MARKER_TYPES.COMMUNITY:
      return item.slug ? `/community/${item.slug}` : `/community/${item.id}`;
    case MARKER_TYPES.EVENT:
      return item.slug ? `/event/${item.slug}` : `/event/${item.id}`;
    default:
      return null;
  }
}

/**
 * Entity info display - reusable across modal layouts
 * Shows: name, artist (hearts), distance, community, type badge
 */

export const FALLBACK_IMAGES = {
  heart: '/images/heart.jpg',
  event: '/images/event.jpg',
  attraction: '/images/attraction.jpg',
  promotion: '/images/pramotion.jpg',
  community: '/images/community.jpg',
  artist: '/images/artist.png'
};
export function EntityInfo(t0) {
  const $ = _c(131);
  const {
    item,
    formattedDistance,
    layout: t1
  } = t0;
  const layout = t1 === undefined ? "stacked" : t1;
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    updateMarkerType
  } = useExplore();
  item?.offerDetails;
  const t2 = item?.offerDetails || "";
  let t3;
  if ($[0] !== t2) {
    t3 = smartTruncate(t2, 300);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    short,
    isTruncated
  } = t3;
  if (!item) {
    return null;
  }
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== formattedDistance || $[3] !== isExpanded || $[4] !== isTruncated || $[5] !== item.artist || $[6] !== item.endDate || $[7] !== item.id || $[8] !== item.locationName || $[9] !== item.locations || $[10] !== item.name || $[11] !== item.occurances || $[12] !== item.offerDetails || $[13] !== item.offerText || $[14] !== item.startDate || $[15] !== item.thumbnail || $[16] !== item.type || $[17] !== item.url || $[18] !== layout || $[19] !== short || $[20] !== updateMarkerType) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const scheduleRows = item.type === "event" && Array.isArray(item.occurances) ? [...item.occurances].filter(_temp).sort(_temp2).map(_temp3).filter(_temp4) : [];
      if (layout === "inline") {
        let t11;
        if ($[28] !== item.name || $[29] !== item.thumbnail) {
          t11 = item.thumbnail && <div className="flex-shrink-0 rounded-[14px] border border-[#DDDDDD]/30 overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100 md:relative -mx-4"><Image src={item.thumbnail} alt={item.name} width={500} height={336} className="object-cover " sizes="500" /></div>;
          $[28] = item.name;
          $[29] = item.thumbnail;
          $[30] = t11;
        } else {
          t11 = $[30];
        }
        let t12;
        if ($[31] !== item.name) {
          t12 = <h2 className="text-[20px] font-semibold text-[#323232] leading-[1.25em]">{item.name}</h2>;
          $[31] = item.name;
          $[32] = t12;
        } else {
          t12 = $[32];
        }
        let t13;
        if ($[33] !== item.artist || $[34] !== item.type) {
          t13 = isHeartType(item.type) && item.artist && <p className="text-sm text-[#626262] flex items-start gap-2 mt-1"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><PenIcon className="w-4 h-4 text-[#BABABA]" /></span>by {item.artist}</p>;
          $[33] = item.artist;
          $[34] = item.type;
          $[35] = t13;
        } else {
          t13 = $[35];
        }
        let t14;
        if ($[36] !== formattedDistance) {
          t14 = formattedDistance && <p className="text-sm text-[#626262] flex items-start gap-2 mt-1"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><Image src={ClockMiles} alt="distance" width={13} height={10} className="w-4 h-auto" /></span>{formattedDistance} away</p>;
          $[36] = formattedDistance;
          $[37] = t14;
        } else {
          t14 = $[37];
        }
        const t15 = scheduleRows.length > 0 && <div className="space-y-0.5 mt-0.5">{scheduleRows.map(_temp5)}</div>;
        let t16;
        if ($[38] !== item.locationName || $[39] !== item.type) {
          t16 = item.locationName && item.type !== "promotion" && <p className="text-sm text-[#626262] flex items-start gap-2"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><IconPin className="w-4 h-4 text-[#BABABA]" /></span>{formatLocationName(item.locationName)}</p>;
          $[38] = item.locationName;
          $[39] = item.type;
          $[40] = t16;
        } else {
          t16 = $[40];
        }
        let t17;
        if ($[41] !== item.locations || $[42] !== item.type) {
          t17 = item.type === "promotion" && item.locations?.length >= 1 && <p className="text-sm text-[#626262] flex items-start gap-2"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><IconPin className="w-4 h-4 text-[#BABABA]" /></span>{item.locations[0].address}</p>;
          $[41] = item.locations;
          $[42] = item.type;
          $[43] = t17;
        } else {
          t17 = $[43];
        }
        let t18;
        if ($[44] !== item.id || $[45] !== item.name || $[46] !== item.type || $[47] !== updateMarkerType) {
          t18 = isHeartType(item.type) && <HeartSaveButton heartId={item?.id} heartName={item?.name} iconOnly={true} className="text-[11px] text-[#323232] gap-1" onSave={isSaved => updateMarkerType(item.id, isSaved ? MARKER_TYPES.SAVED_HEART : MARKER_TYPES.HEART)} />;
          $[44] = item.id;
          $[45] = item.name;
          $[46] = item.type;
          $[47] = updateMarkerType;
          $[48] = t18;
        } else {
          t18 = $[48];
        }
        let t19;
        if ($[49] !== item.type) {
          t19 = <TypeBadge type={item.type} className="!items-center text-[11px] hidden lg:flex" />;
          $[49] = item.type;
          $[50] = t19;
        } else {
          t19 = $[50];
        }
        let t20;
        if ($[51] !== t18 || $[52] !== t19) {
          t20 = <div className="flex-shrink-0 flex flex-col gap-1 items-end top-0 absolute right-0">{t18}{t19}</div>;
          $[51] = t18;
          $[52] = t19;
          $[53] = t20;
        } else {
          t20 = $[53];
        }
        let t21;
        if ($[54] !== t12 || $[55] !== t13 || $[56] !== t14 || $[57] !== t15 || $[58] !== t16 || $[59] !== t17 || $[60] !== t20) {
          t21 = <div className="flex-1 min-w-0 relative mt-[14px]">{t12}{t13}{t14}{t15}{t16}{t17}{t20}</div>;
          $[54] = t12;
          $[55] = t13;
          $[56] = t14;
          $[57] = t15;
          $[58] = t16;
          $[59] = t17;
          $[60] = t20;
          $[61] = t21;
        } else {
          t21 = $[61];
        }
        let t22;
        if ($[62] !== t11 || $[63] !== t21) {
          t22 = <div className="lg:flex gap-3">{t11}{t21}</div>;
          $[62] = t11;
          $[63] = t21;
          $[64] = t22;
        } else {
          t22 = $[64];
        }
        let t23;
        if ($[65] !== isExpanded || $[66] !== isTruncated || $[67] !== item.endDate || $[68] !== item.locations || $[69] !== item.offerDetails || $[70] !== item.offerText || $[71] !== item.startDate || $[72] !== item.type || $[73] !== item.url || $[74] !== short) {
          t23 = item.type === "promotion" && <div className="pt-4 space-y-3 mt-4 border-t border-gray-100">{item.offerText && <h3 className="text-base text-[#323232] font-semibold leading-[1.2em]">{item.offerText}</h3>}{item.offerDetails && <div id="map-modal-offer-details-mobile" className="text-[#323232] leading-[1.6] my-4 text-left"><span className="text-sm whitespace-pre-wrap inline">{isExpanded || !isTruncated ? item.offerDetails : `${short}…`}</span>{isTruncated && <button type="button" aria-expanded={isExpanded} aria-controls="map-modal-offer-details-mobile" aria-label={isExpanded ? "Collapse to hide additional content" : "Expand to read more content"} onClick={() => setIsExpanded(_temp6)} className="inline items-center cursor-pointer gap-1 text-base font-medium text-[#626262] hover:underline focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-sky-600">{isExpanded ? "less" : "more"}</button>}</div>}{item.locations && item.locations.length > 1 && <div className="pt-3 border-t border-gray-200"><p className="text-[#323232] font-semibold mb-2 text-sm">Available at {item.locations.length} location{item.locations.length > 1 ? "s" : ""}:</p><div className="space-y-2 max-h-32 overflow-y-auto">{item.locations.map(_temp7)}</div></div>}{item.url && <div className="pt-2"><p className="text-[#757575] block text-[15px]">Company Website:</p><Link href={item.url} target="_blank" rel="noopener noreferrer" className="!text-[#757575] !text-[15px] underline break-all">{item.url}</Link></div>}{(item.startDate || item.endDate) && <div className="pt-2"><p className="text-[#757575] block text-[15px]">Valid Through</p><p className="text-[#323232] font-semibold text-xs">{fmtDate(item.startDate)} - {fmtDate(item.endDate)}</p></div>}</div>;
          $[65] = isExpanded;
          $[66] = isTruncated;
          $[67] = item.endDate;
          $[68] = item.locations;
          $[69] = item.offerDetails;
          $[70] = item.offerText;
          $[71] = item.startDate;
          $[72] = item.type;
          $[73] = item.url;
          $[74] = short;
          $[75] = t23;
        } else {
          t23 = $[75];
        }
        let t24;
        if ($[76] !== t22 || $[77] !== t23) {
          t24 = <>{t22}{t23}</>;
          $[76] = t22;
          $[77] = t23;
          $[78] = t24;
        } else {
          t24 = $[78];
        }
        t10 = t24;
        break bb0;
      }
      t9 = "pt-3.5 flex gap-3 justify-between";
      t4 = "flex-1 space-y-1.5";
      if ($[79] !== item.name) {
        t5 = <h2 className="text-2xl font-semibold text-[#323232] leading-[1em] mb-2.5">{item.name}</h2>;
        $[79] = item.name;
        $[80] = t5;
      } else {
        t5 = $[80];
      }
      if ($[81] !== item.artist || $[82] !== item.type) {
        t6 = isHeartType(item.type) && item.artist && <p className="text-sm text-[#626262] flex items-start gap-2.5"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><PenIcon className="w-4 h-4 text-[#BABABA]" /></span>by {item.artist}</p>;
        $[81] = item.artist;
        $[82] = item.type;
        $[83] = t6;
      } else {
        t6 = $[83];
      }
      if ($[84] !== formattedDistance) {
        t7 = formattedDistance && <p className="text-sm text-[#626262] flex items-start gap-2.5"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><Image src={ClockMiles} alt="distance" width={13} height={10} className="w-4 h-auto" /></span>{formattedDistance} away</p>;
        $[84] = formattedDistance;
        $[85] = t7;
      } else {
        t7 = $[85];
      }
      t8 = scheduleRows.length > 0 && <div className="space-y-1">{scheduleRows.map(_temp8)}</div>;
    }
    $[2] = formattedDistance;
    $[3] = isExpanded;
    $[4] = isTruncated;
    $[5] = item.artist;
    $[6] = item.endDate;
    $[7] = item.id;
    $[8] = item.locationName;
    $[9] = item.locations;
    $[10] = item.name;
    $[11] = item.occurances;
    $[12] = item.offerDetails;
    $[13] = item.offerText;
    $[14] = item.startDate;
    $[15] = item.thumbnail;
    $[16] = item.type;
    $[17] = item.url;
    $[18] = layout;
    $[19] = short;
    $[20] = updateMarkerType;
    $[21] = t10;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t10 = $[21];
    t4 = $[22];
    t5 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[86] !== item.locationName || $[87] !== item.type) {
    t11 = item.locationName && item.type !== "promotion" && <p className="text-sm text-[#626262] flex items-start gap-2.5"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><IconPin className="w-4 h-4 text-[#BABABA]" /></span>{formatLocationName(item.locationName)}</p>;
    $[86] = item.locationName;
    $[87] = item.type;
    $[88] = t11;
  } else {
    t11 = $[88];
  }
  let t12;
  if ($[89] !== item.locations || $[90] !== item.type) {
    t12 = item.type === "promotion" && item.locations?.length >= 1 && <p className="text-sm text-[#626262] flex items-start gap-2.5"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><IconPin className="w-4 h-4 text-[#BABABA]" /></span>{item.locations[0].address}</p>;
    $[89] = item.locations;
    $[90] = item.type;
    $[91] = t12;
  } else {
    t12 = $[91];
  }
  let t13;
  if ($[92] !== t11 || $[93] !== t12 || $[94] !== t4 || $[95] !== t5 || $[96] !== t6 || $[97] !== t7 || $[98] !== t8) {
    t13 = <div className={t4}>{t5}{t6}{t7}{t8}{t11}{t12}</div>;
    $[92] = t11;
    $[93] = t12;
    $[94] = t4;
    $[95] = t5;
    $[96] = t6;
    $[97] = t7;
    $[98] = t8;
    $[99] = t13;
  } else {
    t13 = $[99];
  }
  const t14 = `pt-1 ${!item?.thumbnail || item.thumbnail.length === 0 ? "flex items-end md:pt-10" : ""}`;
  let t15;
  if ($[100] !== item.type) {
    t15 = <TypeBadge type={item.type} className="gap-1 !items-center leading-[1em] text-sm" badgeClass="w-5 h-5" />;
    $[100] = item.type;
    $[101] = t15;
  } else {
    t15 = $[101];
  }
  let t16;
  if ($[102] !== item.id || $[103] !== item.name || $[104] !== item.type || $[105] !== updateMarkerType) {
    t16 = isHeartType(item.type) && <><p className="h-[0.00001em] text-[#626262] flex items-start gap-2 mt-1"> </p><HeartSaveButton heartId={item?.id} heartName={item?.name} iconOnly={true} className="text-[13px] text-[#323232] gap-1" onSave={isSaved_0 => updateMarkerType(item.id, isSaved_0 ? "heartSaved" : "heart")} /></>;
    $[102] = item.id;
    $[103] = item.name;
    $[104] = item.type;
    $[105] = updateMarkerType;
    $[106] = t16;
  } else {
    t16 = $[106];
  }
  let t17;
  if ($[107] !== t15 || $[108] !== t16) {
    t17 = <div className="flex flex-col gap-1 items-end">{t15}{t16}</div>;
    $[107] = t15;
    $[108] = t16;
    $[109] = t17;
  } else {
    t17 = $[109];
  }
  let t18;
  if ($[110] !== t14 || $[111] !== t17) {
    t18 = <div className={t14}>{t17}</div>;
    $[110] = t14;
    $[111] = t17;
    $[112] = t18;
  } else {
    t18 = $[112];
  }
  let t19;
  if ($[113] !== t13 || $[114] !== t18 || $[115] !== t9) {
    t19 = <div className={t9}>{t13}{t18}</div>;
    $[113] = t13;
    $[114] = t18;
    $[115] = t9;
    $[116] = t19;
  } else {
    t19 = $[116];
  }
  let t20;
  if ($[117] !== isExpanded || $[118] !== isTruncated || $[119] !== item.endDate || $[120] !== item.locations || $[121] !== item.offerDetails || $[122] !== item.offerText || $[123] !== item.startDate || $[124] !== item.type || $[125] !== item.url || $[126] !== short) {
    t20 = item.type === "promotion" && <div className="pt-4 space-y-3">{item.offerText && <h3 className="text-lg text-[#323232] font-semibold leading-[1.2em]">{item.offerText}</h3>}{item.offerDetails && (() => <div id="map-modal-offer-details-desktop" className="text-[#323232] leading-[1.6] text-left"><span className="text-[15px] whitespace-pre-wrap inline">{isExpanded || !isTruncated ? item.offerDetails : `${short}…`}</span>{isTruncated && <button type="button" aria-expanded={isExpanded} aria-controls="map-modal-offer-details-desktop" aria-label={isExpanded ? "Collapse to hide additional content" : "Expand to read more content"} onClick={() => setIsExpanded(_temp9)} className="inline items-center cursor-pointer gap-1 text-base font-medium text-[#626262] hover:underline focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-sky-600">{isExpanded ? "less" : "more"}</button>}</div>)()}{item.locations && item.locations.length > 1 && <div className="pt-3 border-t border-gray-200"><p className="text-[#323232] font-semibold mb-3 text-[15px]">Available at {item.locations.length} location{item.locations.length > 1 ? "s" : ""}:</p><div className="space-y-2.5 max-h-40 overflow-y-auto">{item.locations.map(_temp10)}</div></div>}{item.url && <div className="pt-3"><p className="text-[#757575] text-[15px] mb-1">Company Website:</p><Link href={item.url} target="_blank" rel="noopener noreferrer" className="text-[#757575] text-[15px] underline break-all hover:text-[#323232]">{item.url}</Link></div>}{(item.startDate || item.endDate) && <div className="pt-3"><p className="text-[#757575] text-[15px] mb-1">Valid Through</p><p className="text-[#323232] font-semibold text-[15px]">{fmtDate(item.startDate)} - {fmtDate(item.endDate)}</p></div>}</div>;
    $[117] = isExpanded;
    $[118] = isTruncated;
    $[119] = item.endDate;
    $[120] = item.locations;
    $[121] = item.offerDetails;
    $[122] = item.offerText;
    $[123] = item.startDate;
    $[124] = item.type;
    $[125] = item.url;
    $[126] = short;
    $[127] = t20;
  } else {
    t20 = $[127];
  }
  let t21;
  if ($[128] !== t19 || $[129] !== t20) {
    t21 = <div>{t19}{t20}</div>;
    $[128] = t19;
    $[129] = t20;
    $[130] = t21;
  } else {
    t21 = $[130];
  }
  return t21;
}

/**
 * Action buttons for entity details
 */
function _temp10(location_0, index_0) {
  const directionsUrl_0 = location_0.coordinates?.[0] && location_0.coordinates?.[1] ? getDirectionsUrl(location_0.coordinates[0], location_0.coordinates[1], location_0.address) : null;
  return <div key={location_0.id || index_0} className="flex items-center gap-2 text-sm">{directionsUrl_0 && <Link href={directionsUrl_0} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2"><span className="w-4 flex-shrink-0 mt-0.5"><svg width="11" height="13" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.032 11.3423C6.33622 10.1748 7.33428 9.05489 8.02617 7.98267C8.71806 6.91044 9.064 5.97133 9.064 5.16533C9.064 3.95 8.67789 2.95089 7.90567 2.168C7.13344 1.38511 6.17556 0.993667 5.032 0.993667C3.88844 0.993667 2.93056 1.38511 2.15833 2.168C1.38611 2.95089 1 3.95 1 5.16533C1 5.97133 1.34594 6.91044 2.03783 7.98267C2.72972 9.05489 3.72778 10.1748 5.032 11.3423ZM5.032 12.2935C4.90211 12.2935 4.77222 12.2711 4.64233 12.2263C4.51233 12.1814 4.39478 12.112 4.28967 12.018C3.69144 11.4667 3.13161 10.8989 2.61017 10.3147C2.08883 9.73056 1.63567 9.14661 1.25067 8.56283C0.865555 7.97906 0.560833 7.40044 0.3365 6.827C0.112167 6.25344 0 5.69955 0 5.16533C0 3.62689 0.497611 2.38139 1.49283 1.42883C2.48817 0.476277 3.66789 0 5.032 0C6.39611 0 7.57583 0.476277 8.57117 1.42883C9.56639 2.38139 10.064 3.62689 10.064 5.16533C10.064 5.69955 9.95183 6.25233 9.7275 6.82367C9.50317 7.39511 9.19956 7.97378 8.81667 8.55967C8.43367 9.14555 7.9815 9.7295 7.46017 10.3115C6.93883 10.8936 6.379 11.4603 5.78067 12.0115C5.67711 12.1055 5.55939 12.176 5.4275 12.223C5.29572 12.27 5.16389 12.2935 5.032 12.2935ZM5.03317 6.2435C5.36483 6.2435 5.64839 6.12539 5.88383 5.88917C6.11939 5.65294 6.23717 5.369 6.23717 5.03733C6.23717 4.70567 6.11906 4.42206 5.88283 4.1865C5.64661 3.95106 5.36261 3.83333 5.03083 3.83333C4.69917 3.83333 4.41561 3.95144 4.18017 4.18767C3.94461 4.42389 3.82683 4.70789 3.82683 5.03967C3.82683 5.37133 3.94494 5.65489 4.18117 5.89033C4.41739 6.12578 4.70139 6.2435 5.03317 6.2435Z" fill="#BABABA" /></svg></span><span className="text-[#626262] text-sm leading-[1.6]">{location_0.address}</span></Link>}</div>;
}
function _temp9(v_0) {
  return !v_0;
}
function _temp8(row_1) {
  return <p key={row_1.dateTime} className="text-sm text-[#626262] flex items-start gap-2.5"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><IconAlarm className="h-4 w-4 text-[#BABABA]" /></span><span>{row_1.label}{row_1.timeText ? `, ${row_1.timeText}` : ""}</span></p>;
}
function _temp7(location, index) {
  const directionsUrl = location.coordinates?.[0] && location.coordinates?.[1] ? getDirectionsUrl(location.coordinates[0], location.coordinates[1], location.address) : null;
  return <div key={location.id || index} className="flex items-center gap-2 text-xs">{directionsUrl && <Link href={directionsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2"><span className="w-3 flex-shrink-0 mt-0.5"><svg width="11" height="13" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.032 11.3423C6.33622 10.1748 7.33428 9.05489 8.02617 7.98267C8.71806 6.91044 9.064 5.97133 9.064 5.16533C9.064 3.95 8.67789 2.95089 7.90567 2.168C7.13344 1.38511 6.17556 0.993667 5.032 0.993667C3.88844 0.993667 2.93056 1.38511 2.15833 2.168C1.38611 2.95089 1 3.95 1 5.16533C1 5.97133 1.34594 6.91044 2.03783 7.98267C2.72972 9.05489 3.72778 10.1748 5.032 11.3423ZM5.032 12.2935C4.90211 12.2935 4.77222 12.2711 4.64233 12.2263C4.51233 12.1814 4.39478 12.112 4.28967 12.018C3.69144 11.4667 3.13161 10.8989 2.61017 10.3147C2.08883 9.73056 1.63567 9.14661 1.25067 8.56283C0.865555 7.97906 0.560833 7.40044 0.3365 6.827C0.112167 6.25344 0 5.69955 0 5.16533C0 3.62689 0.497611 2.38139 1.49283 1.42883C2.48817 0.476277 3.66789 0 5.032 0C6.39611 0 7.57583 0.476277 8.57117 1.42883C9.56639 2.38139 10.064 3.62689 10.064 5.16533C10.064 5.69955 9.95183 6.25233 9.7275 6.82367C9.50317 7.39511 9.19956 7.97378 8.81667 8.55967C8.43367 9.14555 7.9815 9.7295 7.46017 10.3115C6.93883 10.8936 6.379 11.4603 5.78067 12.0115C5.67711 12.1055 5.55939 12.176 5.4275 12.223C5.29572 12.27 5.16389 12.2935 5.032 12.2935ZM5.03317 6.2435C5.36483 6.2435 5.64839 6.12539 5.88383 5.88917C6.11939 5.65294 6.23717 5.369 6.23717 5.03733C6.23717 4.70567 6.11906 4.42206 5.88283 4.1865C5.64661 3.95106 5.36261 3.83333 5.03083 3.83333C4.69917 3.83333 4.41561 3.95144 4.18017 4.18767C3.94461 4.42389 3.82683 4.70789 3.82683 5.03967C3.82683 5.37133 3.94494 5.65489 4.18117 5.89033C4.41739 6.12578 4.70139 6.2435 5.03317 6.2435Z" fill="#BABABA" /></svg></span><span className="text-[#626262] text-sm leading-[1.6]">{location.address}</span></Link>}</div>;
}
function _temp6(v) {
  return !v;
}
function _temp5(row_0) {
  return <p key={row_0.dateTime} className="text-sm text-[#626262] flex items-start gap-2"><span className="flex justify-center items-center w-5.5 h-5.5 shrink-0"><IconAlarm className="h-4 w-4 text-[#BABABA]" /></span><span>{row_0.label}{row_0.timeText ? `, ${row_0.timeText}` : ""}</span></p>;
}
function _temp4(row) {
  return row.label || row.timeText;
}
function _temp3(occ_0) {
  const {
    month,
    day
  } = monthDay(occ_0.date);
  const numericDay = Number(day);
  const label = month && numericDay ? `${month} ${ordinal(numericDay)}` : null;
  const timeText = fmtTimeRangeFromStrings(occ_0.start, occ_0.end, occ_0.date);
  return {
    label: label || null,
    timeText,
    dateTime: combineDateAndTime(occ_0.date, occ_0.start)
  };
}
function _temp2(a, b) {
  return new Date(a.date) - new Date(b.date);
}
function _temp(occ) {
  return occ?.start;
}
export function EntityActions(t0) {
  const $ = _c(16);
  const {
    item,
    directionsUrl,
    coordinates,
    isMobile
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = getDetailsUrl(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const detailsUrl = t1;
  const t2 = item?.type;
  let t3;
  if ($[2] !== t2) {
    t3 = getMarkerIcon(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const iconConfig = t3;
  let t4;
  if ($[4] !== iconConfig?.detailsButtonText || $[5] !== item?.type) {
    t4 = isHeartType(item?.type) ? "Heart Details" : `${iconConfig?.detailsButtonText || "View"} Details`;
    $[4] = iconConfig?.detailsButtonText;
    $[5] = item?.type;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const detailsButtonText = t4;
  const userAgent = typeof window !== "undefined" ? window.navigator.userAgent : "";
  const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroidDevice = /Android/.test(userAgent);
  let t5;
  bb0: {
    if (!isMobile || !coordinates) {
      t5 = null;
      break bb0;
    }
    const label = encodeURIComponent(item?.name || "");
    if (isIOSDevice) {
      t5 = `http://maps.apple.com/?daddr=${coordinates.lat},${coordinates.lng}${label ? `&q=${label}` : ""}`;
      break bb0;
    }
    if (isAndroidDevice) {
      t5 = `geo:${coordinates.lat},${coordinates.lng}?q=${coordinates.lat},${coordinates.lng}${label ? `(${label})` : ""}`;
      break bb0;
    }
    t5 = directionsUrl;
  }
  const mobileDirectionsHref = t5;
  const desktopDirectionsHref = !isMobile ? directionsUrl : null;
  const directionsHref = mobileDirectionsHref || desktopDirectionsHref;
  let t6;
  if ($[7] !== directionsHref || $[8] !== isMobile) {
    t6 = directionsHref && <UIButton href={directionsHref} variant="gradient" className="!min-w-[100%] !text-base !justify-center min-h-12 !font-semibold" target={!isMobile ? "_blank" : undefined} rel={!isMobile ? "noopener noreferrer" : undefined}>Directions</UIButton>;
    $[7] = directionsHref;
    $[8] = isMobile;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== detailsButtonText || $[11] !== detailsUrl) {
    t7 = detailsUrl && <UIButton href={detailsUrl} variant="primaryTheme" className="!rounded-full !min-w-[100%] !text-base !text-center items-center leading-8 min-h-12 !font-semibold">{detailsButtonText}</UIButton>;
    $[10] = detailsButtonText;
    $[11] = detailsUrl;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="flex flex-col gap-3 pt-4 md:pb-2">{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

/**
 * Hero image for entity
 */
export function EntityHero(t0) {
  const $ = _c(3);
  const {
    item
  } = t0;
  if (!item?.thumbnail) {
    return null;
  }
  let t1;
  if ($[0] !== item.name || $[1] !== item.thumbnail) {
    t1 = <div className="border border-[#DDDDDD]/70 shrink-0 rounded-b-[14px] rounded-tl-[14px] rounded-tr-none bg-[#F6F6F6] overflow-hidden max-h-[306px] flex items-center justify-center"><Image src={item.thumbnail} alt={item.name} width={640} height={341} className="max-w-full object-contain object-top max-h-[306px] rounded-tr-none" /></div>;
    $[0] = item.name;
    $[1] = item.thumbnail;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Modal/Drawer component for displaying map pin details
 * Desktop: slides from right | Mobile: slides from bottom
 *
 * Uses composable EntityInfo, EntityActions, EntityHero components
 * that can be reused in PhotosView or other contexts
 */
export function MapPinModal(t0) {
  const $ = _c(76);
  const {
    item,
    isOpen,
    onClose,
    pinPosition: t1
  } = t0;
  const pinPosition = t1 === undefined ? null : t1;
  const [isMobile, setIsMobile] = useState(_temp11);
  let {
    formattedDistance,
    directionsUrl,
    coordinates
  } = useItemLocation(item);
  if (item?.type === "promotion" && item?.locations?.length >= 1) {
    const firstLocation = item.locations[0];
    if (firstLocation.coordinates?.[0] && firstLocation.coordinates?.[1]) {
      let t2;
      if ($[0] !== firstLocation.coordinates[0] || $[1] !== firstLocation.coordinates[1]) {
        t2 = {
          lat: firstLocation.coordinates[0],
          lng: firstLocation.coordinates[1]
        };
        $[0] = firstLocation.coordinates[0];
        $[1] = firstLocation.coordinates[1];
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      coordinates = t2;
      const t3 = firstLocation.address || item.name;
      let t4;
      if ($[3] !== firstLocation.coordinates[0] || $[4] !== firstLocation.coordinates[1] || $[5] !== t3) {
        t4 = getDirectionsUrl(firstLocation.coordinates[0], firstLocation.coordinates[1], t3);
        $[3] = firstLocation.coordinates[0];
        $[4] = firstLocation.coordinates[1];
        $[5] = t3;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      directionsUrl = t4;
    } else {
      coordinates = null;
      directionsUrl = null;
    }
  }
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  let t2;
  let t3;
  if ($[7] !== isOpen || $[8] !== onClose) {
    t2 = () => {
      if (!isOpen) {
        return;
      }
      let cancelled = false;
      const focusCloseButton = () => {
        if (cancelled) {
          return;
        }
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        } else {
          requestAnimationFrame(focusCloseButton);
        }
      };
      focusCloseButton();
      const handleKeyDown = e => {
        if (!modalRef.current) {
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
          return;
        }
        if (e.key === "Tab") {
          const focusable = modalRef.current.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex=\"-1\"])");
          if (!focusable.length) {
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else {
            if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        cancelled = true;
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t3 = [isOpen, onClose];
    $[7] = isOpen;
    $[8] = onClose;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    };
    t5 = [];
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  useEffect(t4, t5);
  const hasThumbnail = !!item?.thumbnail && item.thumbnail.length > 0;
  if (!item) {
    return null;
  }
  if (!isMobile) {
    const hasDesktopThumbnail = !!item?.thumbnail;
    let wrapperClasses = `absolute inset-0 z-30 flex items-center justify-center ${isOpen ? "" : "hidden"}`;
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        maxHeight: "min(85dvh, 720px)"
      };
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let cardStyle = t6;
    let cardClasses = "relative w-[430px] pointer-events-auto rounded-[14px] border border-[#DDDDDD] bg-white shadow-[-2px_2px_24px_0_rgba(0,0,0,0.16)] overflow-hidden flex flex-col";
    if (pinPosition) {
      const {
        x,
        y,
        containerWidth,
        containerHeight
      } = pinPosition;
      let left = x - 215;
      left = Math.max(16, Math.min(left, containerWidth - 430 - 16));
      const availableHeight = Math.max(y - 12 - 16, 100);
      const maxH = Math.min(availableHeight, containerHeight * 0.85, 720);
      const t7 = `${left}px`;
      const t8 = `${y - 12}px`;
      const t9 = `${maxH}px`;
      let t10;
      if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
        t10 = {
          position: "absolute",
          left: t7,
          top: t8,
          transform: "translateY(-100%)",
          maxHeight: t9,
          width: "430px"
        };
        $[14] = t7;
        $[15] = t8;
        $[16] = t9;
        $[17] = t10;
      } else {
        t10 = $[17];
      }
      cardStyle = t10;
      cardClasses = "pointer-events-auto rounded-[14px] border border-[#DDDDDD] bg-white shadow-[-2px_2px_24px_0_rgba(0,0,0,0.16)] overflow-hidden flex flex-col";
      wrapperClasses = `absolute inset-0 z-30 pointer-events-none ${isOpen ? "" : "hidden"}`;
    }
    let t7;
    if ($[18] !== onClose || $[19] !== pinPosition) {
      t7 = !pinPosition && <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />;
      $[18] = onClose;
      $[19] = pinPosition;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const t8 = hasDesktopThumbnail ? "light" : "dark";
    let t9;
    if ($[21] !== onClose || $[22] !== t8) {
      t9 = <CloseButton ref={closeButtonRef} onClick={onClose} variant={t8} />;
      $[21] = onClose;
      $[22] = t8;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] !== hasDesktopThumbnail || $[25] !== item) {
      t10 = hasDesktopThumbnail && <EntityHero item={item} />;
      $[24] = hasDesktopThumbnail;
      $[25] = item;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== formattedDistance || $[28] !== item) {
      t11 = <EntityInfo item={item} formattedDistance={formattedDistance} layout="stacked" />;
      $[27] = formattedDistance;
      $[28] = item;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== coordinates || $[31] !== directionsUrl || $[32] !== item) {
      t12 = <EntityActions item={item} directionsUrl={directionsUrl} coordinates={coordinates} isMobile={false} />;
      $[30] = coordinates;
      $[31] = directionsUrl;
      $[32] = item;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== t11 || $[35] !== t12) {
      t13 = <div className="xl:px-7.5 md:px-6 px-4 xl:pt-4 md:pt-2.5 pt-5 pb-4 space-y-3 flex flex-col">{t11}{t12}</div>;
      $[34] = t11;
      $[35] = t12;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    let t14;
    if ($[37] !== t10 || $[38] !== t13 || $[39] !== t9) {
      t14 = <div ref={modalRef} className="flex-1 overflow-y-auto flex flex-col">{t9}{t10}{t13}</div>;
      $[37] = t10;
      $[38] = t13;
      $[39] = t9;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    let t15;
    if ($[41] !== cardClasses || $[42] !== cardStyle || $[43] !== t14) {
      t15 = <div className={cardClasses} style={cardStyle}>{t14}</div>;
      $[41] = cardClasses;
      $[42] = cardStyle;
      $[43] = t14;
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    let t16;
    if ($[45] !== t15 || $[46] !== t7 || $[47] !== wrapperClasses) {
      t16 = <div className={wrapperClasses}>{t7}{t15}</div>;
      $[45] = t15;
      $[46] = t7;
      $[47] = wrapperClasses;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    return t16;
  }
  const t6 = `bg-white ${isMobile ? "rounded-t-2xl" : "h-full overflow-y-auto rounded-l-[14px] flex flex-col"}`;
  const t7 = hasThumbnail ? "light" : "dark";
  let t8;
  if ($[49] !== onClose || $[50] !== t7) {
    t8 = <CloseButton ref={closeButtonRef} onClick={onClose} variant={t7} />;
    $[49] = onClose;
    $[50] = t7;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  let t9;
  if ($[52] !== isMobile || $[53] !== item) {
    t9 = !isMobile && item.thumbnail && <EntityHero item={item} />;
    $[52] = isMobile;
    $[53] = item;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  const t10 = isMobile ? "inline" : "stacked";
  let t11;
  if ($[55] !== formattedDistance || $[56] !== item || $[57] !== t10) {
    t11 = <EntityInfo item={item} formattedDistance={formattedDistance} layout={t10} />;
    $[55] = formattedDistance;
    $[56] = item;
    $[57] = t10;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  let t12;
  if ($[59] !== coordinates || $[60] !== directionsUrl || $[61] !== isMobile || $[62] !== item) {
    t12 = <EntityActions item={item} directionsUrl={directionsUrl} coordinates={coordinates} isMobile={isMobile} />;
    $[59] = coordinates;
    $[60] = directionsUrl;
    $[61] = isMobile;
    $[62] = item;
    $[63] = t12;
  } else {
    t12 = $[63];
  }
  let t13;
  if ($[64] !== t11 || $[65] !== t12) {
    t13 = <div className="xl:px-7.5 md:px-6 px-4 xl:pt-4 md:pt-2.5 pt-0 pb-4 space-y-3 h-full flex flex-col ">{t11}{t12}</div>;
    $[64] = t11;
    $[65] = t12;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  let t14;
  if ($[67] !== t13 || $[68] !== t6 || $[69] !== t8 || $[70] !== t9) {
    t14 = <div ref={modalRef} className={t6}>{t8}{t9}{t13}</div>;
    $[67] = t13;
    $[68] = t6;
    $[69] = t8;
    $[70] = t9;
    $[71] = t14;
  } else {
    t14 = $[71];
  }
  let t15;
  if ($[72] !== isOpen || $[73] !== onClose || $[74] !== t14) {
    t15 = <Drawer isOpen={isOpen} onClose={onClose} direction="bottom" className="rounded-t-2xl max-h-[95dvh] overflow-y-auto no-scrollbar" closeOnOverlayClick={true}>{t14}</Drawer>;
    $[72] = isOpen;
    $[73] = onClose;
    $[74] = t14;
    $[75] = t15;
  } else {
    t15 = $[75];
  }
  return t15;
}

/**
 * Close button component
 */
function _temp11() {
  return typeof window !== "undefined" && window.innerWidth < 768;
}
export const CloseButton = forwardRef((t0, ref) => {
  const $ = _c(7);
  const {
    onClick,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "light" : t1;
  const isLight = variant === "light";
  const t2 = `absolute z-20 xl:w-12 xl:h-12 md:w-10.5 md:h-10.5 w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer top-3 right-3 
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black 
        ${isLight ? "md:bg-black/60 md:hover:bg-black/80 bg-[#F1F1F1] hover:bg-white shadow-md" : " md:bg-[#00EAFD] md:hover:bg-[#00d5e7] bg-[#F1F1F1] hover:bg-white shadow-md"}`;
  const t3 = `md:w-5 md:h-5 w-3.5 h-3.5 ${isLight ? "md:text-white text-[#323232]" : "text-[#323232]"}`;
  let t4;
  if ($[0] !== t3) {
    t4 = <CloseRoundIcon className={t3} />;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== onClick || $[3] !== ref || $[4] !== t2 || $[5] !== t4) {
    t5 = <button ref={ref} onClick={onClick} aria-label="Close" className={t2}>{t4}</button>;
    $[2] = onClick;
    $[3] = ref;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
});
CloseButton.displayName = 'CloseButton';